import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Copy, Plus, RefreshCw } from 'lucide-react'
import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  Drawer,
  Input,
  PartnerPicker,
  Select,
  Textarea,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  toast,
  type PickerValue,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { useDocumentTypes } from '../api/queries'
import { useCreateVendorRequest, useVendorRequests } from '../api/vendor-request-queries'
import type { VendorRequestStatus, VendorRequestSummary } from '../api/vendor-request-types'
import { formatDate } from '../utils/dates'
import { EDIT_ROLES, VENDOR_STATUS_LABEL, VENDOR_STATUS_VARIANT } from '../utils/onboarding-meta'

const ALWAYS = ['sds', 'sample_cofa']

/**
 * Product request sheets sent to vendors. Ops creates one, copies the link
 * (shown once) and emails it from their own mailbox — the system never
 * emails vendors.
 */
export function VendorRequestsList({ onOpen }: { onOpen: (id: string) => void }) {
  const canEdit = useHasAnyPermission([...EDIT_ROLES])
  const [status, setStatus] = useState<VendorRequestStatus | ''>('')
  const [createOpen, setCreateOpen] = useState(false)
  const [nonce, setNonce] = useState(0)
  const { data, isLoading, isError, error, refetch, isFetching } = useVendorRequests(status)
  const rows = data?.items ?? []

  const columns = useMemo<ColumnDef<VendorRequestSummary, unknown>[]>(
    () => [
      { id: 'ref', header: 'Request', cell: (ctx) => ctx.row.original.reference },
      { id: 'vendor', header: 'Vendor', cell: (ctx) => ctx.row.original.vendor_name },
      { id: 'product', header: 'Product', cell: (ctx) => ctx.row.original.product_description },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => (
          <Chip variant={VENDOR_STATUS_VARIANT[ctx.row.original.status]} dot>
            {VENDOR_STATUS_LABEL[ctx.row.original.status]}
          </Chip>
        ),
      },
      {
        id: 'expires',
        header: 'Link expires',
        cell: (ctx) => (ctx.row.original.status === 'open' ? formatDate(ctx.row.original.expires_at) : '—'),
      },
      { id: 'submitted', header: 'Submitted', cell: (ctx) => formatDate(ctx.row.original.submitted_at) },
      { id: 'by', header: 'Requested by', cell: (ctx) => ctx.row.original.created_by },
    ],
    [],
  )

  return (
    <>
      <Toolbar>
        <ToolbarLeft>
          <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as VendorRequestStatus | '')}>
            <option value="">All</option>
            {(Object.keys(VENDOR_STATUS_LABEL) as VendorRequestStatus[]).map((s) => (
              <option key={s} value={s}>{VENDOR_STATUS_LABEL[s]}</option>
            ))}
          </Select>
        </ToolbarLeft>
        <ToolbarRight>
          <ToolbarCount>{rows.length} requests</ToolbarCount>
          <Button variant="ghost" size="sm" iconOnly aria-label="Refresh" loading={isFetching && !isLoading}
            onClick={() => void refetch()}>
            <RefreshCw size={14} />
          </Button>
          {canEdit ? (
            <Button variant="primary" size="sm" onClick={() => { setNonce((n) => n + 1); setCreateOpen(true) }}>
              <Plus size={14} /> Request from vendor
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>
      <div className="flex min-h-0 flex-1 flex-col bg-canvas p-4">
        <DataTable
          data={rows}
          columns={columns}
          loading={isLoading}
          error={isError ? error : undefined}
          onRetry={() => void refetch()}
          errorSubject="vendor requests"
          getRowId={(r) => r.id}
          onRowClick={(r) => onOpen(r.id)}
          moduleId="products-vendor-requests"
        />
      </div>
      <CreateVendorRequestDrawer key={nonce} open={createOpen} onOpenChange={setCreateOpen} onOpen={onOpen} />
    </>
  )
}

function CreateVendorRequestDrawer({ open, onOpenChange, onOpen }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpen: (id: string) => void
}) {
  const { data: types = [] } = useDocumentTypes()
  const requestable = types.filter((t) => t.vendor_requestable)
  const create = useCreateVendorRequest()
  const [vendor, setVendor] = useState<PickerValue>(null)
  const [product, setProduct] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [docTypes, setDocTypes] = useState<string[]>([])
  const [message, setMessage] = useState('')
  const [result, setResult] = useState<{ id: string; reference: string; link: string } | null>(null)

  const toggle = (key: string, on: boolean) => setDocTypes((d) => (on ? [...d, key] : d.filter((k) => k !== key)))
  const canCreate = !!vendor && !!product.trim() && !create.isPending

  const submit = () => {
    if (!vendor) return
    create.mutate(
      {
        vendor_partner_id: vendor[0],
        vendor_name: vendor[1],
        product_description: product.trim(),
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        requested_doc_types: docTypes,
        message_to_vendor: message.trim() || null,
      },
      {
        onSuccess: (res) => setResult({ id: res.request.id, reference: res.request.reference, link: res.link }),
        onError: (err) => toast.danger('Could not create the request', { description: (err as Error).message }),
      },
    )
  }

  const copy = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.link)
      toast.success('Link copied')
    } catch {
      toast.warning('Copy failed; select the link and copy it')
    }
  }

  return (
    <Drawer
      surface="form"
      open={open}
      onOpenChange={onOpenChange}
      title={result ? `${result.reference} created` : 'Request product information from a vendor'}
      meta={result ? 'Copy the link and send it to the vendor from your mailbox' : undefined}
      footer={
        <div className="flex justify-end gap-2">
          {result ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              <Button variant="primary" onClick={() => { onOpenChange(false); onOpen(result.id) }}>Open request</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button variant="primary" disabled={!canCreate} loading={create.isPending} onClick={submit}>Create link</Button>
            </>
          )}
        </div>
      }
    >
      {result ? (
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-secondary">
            This link is shown only now. Anyone with it can fill in this request until it expires, so send it only to the
            vendor's contact. If it's lost, create a new link from the request.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={result.link} aria-label="Vendor link" onFocus={(e) => e.currentTarget.select()} />
            <Button variant="secondary" onClick={() => void copy()}><Copy size={14} /> Copy</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 p-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Vendor</span>
            <PartnerPicker kind="vendor" value={vendor} onChange={setVendor} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Product (as the vendor would recognise it)</span>
            <Input value={product} maxLength={255} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. Rebaudioside A 97%" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-secondary">Vendor contact (for your records)</span>
              <Input value={contactName} maxLength={255} onChange={(e) => setContactName(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm text-secondary">Contact email (not emailed by the system)</span>
              <Input type="email" value={contactEmail} maxLength={255} onChange={(e) => setContactEmail(e.target.value)} />
            </label>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm text-secondary">Documents to request</span>
            <div className="grid grid-cols-2 gap-1">
              {requestable.map((t) => (
                <Checkbox
                  key={t.key}
                  label={t.label + (ALWAYS.includes(t.key) ? ' (always)' : '')}
                  checked={ALWAYS.includes(t.key) || docTypes.includes(t.key)}
                  disabled={ALWAYS.includes(t.key)}
                  onChange={(e) => toggle(t.key, e.target.checked)}
                />
              ))}
            </div>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Note shown to the vendor (optional)</span>
            <Textarea rows={2} value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} />
          </label>
          <p className="text-sm text-secondary">The link works for 14 days.</p>
        </div>
      )}
    </Drawer>
  )
}
